import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User, LoginRequest, RegisterRequest } from '../types';
import * as authApi from '../api/auth';
import * as userApi from '../api/user';
import { TOKEN_KEYS } from '../api/client';
import posthog from '../lib/posthog';
import { useLanguage } from '../components/LanguageProvider';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  loginWithGoogleOneTap: (credential: string) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setLanguage } = useLanguage();

  const isAuthenticated = user !== null;

  const applyAuthenticatedUser = useCallback((nextUser: User) => {
    setUser(nextUser);
    if (nextUser.language) {
      setLanguage(nextUser.language);
    }
  }, [setLanguage]);

  // Refresh user data from API
  const refreshUser = useCallback(async () => {
    try {
      const userData = await userApi.getCurrentUser();
      applyAuthenticatedUser(userData);
    } catch {
      // If fetching user fails, clear auth state
      setUser(null);
      localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    }
  }, [applyAuthenticatedUser]);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
      if (token) {
        try {
          await refreshUser();
        } catch {
          // Token invalid, state already cleared by refreshUser
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [refreshUser]);

  // Login with email and password
  const login = useCallback(async (email: string, password: string, turnstileToken?: string) => {
    const loginData: LoginRequest = {
      email,
      password,
      device_info: navigator.userAgent,
      turnstile_token: turnstileToken,
    };
    const response = await authApi.login(loginData);
    applyAuthenticatedUser(response.user);
    posthog.identify(String(response.user.id), {
      email: response.user.email,
      name: response.user.name,
    });
    posthog.capture('user_logged_in', { method: 'email' });
  }, [applyAuthenticatedUser]);

  // Login with Google One Tap credential
  const loginWithGoogleOneTap = useCallback(async (credential: string) => {
    const response = await authApi.loginWithGoogleOneTap(credential);
    applyAuthenticatedUser(response.user);
    posthog.identify(String(response.user.id), {
      email: response.user.email,
      name: response.user.name,
    });
    posthog.capture('user_logged_in', { method: 'google' });
  }, [applyAuthenticatedUser]);

  // Register new user
  const register = useCallback(async (data: RegisterRequest) => {
    const response = await authApi.register(data);
    applyAuthenticatedUser(response.user);
    posthog.identify(String(response.user.id), {
      email: response.user.email,
      name: response.user.name,
      $set_once: { first_signup_at: new Date().toISOString() },
    });
    posthog.capture('user_signed_up', {
      method: 'email',
      has_referral_code: Boolean(data.referral_code),
    });
  }, [applyAuthenticatedUser]);

  // Logout current user
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      posthog.capture('user_logged_out');
      posthog.reset();
      setUser(null);
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    loginWithGoogleOneTap,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
